import {
  createContext,
  FunctionComponent,
  PropsWithChildren,
  useContext,
} from 'react';
import { AuthProvider, useSession } from '@descope/react-sdk';
import { EnvService } from '@hub/services/core/env-service';
import * as Sentry from '@sentry/react';
import { useGetFeatureFlag } from '@monorepo/feature-flag/client';
import { FLAG } from '@monorepo/feature-flag/shared';
import { IDP_PROVIDER, IdpProviderKey } from '@monorepo/types';

/**
 * Context to expose Descope session loading state
 */
const DescopeSessionContext = createContext<{
  isSessionLoading: boolean;
}>({
  isSessionLoading: false, // Default to false when Descope is not active
});

/**
 * Hook to check if Descope session is loading
 * Only works when inside AuthProvider (when Descope is enabled)
 */
export const useDescopeSessionLoading = (): boolean => {
  return useContext(DescopeSessionContext).isSessionLoading;
};

/**
 * Inner component that uses useSession hook safely (only inside AuthProvider)
 */
const DescopeSessionTracker: FunctionComponent<PropsWithChildren> = ({
  children,
}) => {
  const { isSessionLoading } = useSession();

  return (
    <DescopeSessionContext.Provider value={{ isSessionLoading }}>
      {children}
    </DescopeSessionContext.Provider>
  );
};

/**
 * Wrapper component that conditionally renders AuthProvider based on IDP provider and feature flags
 * Renders AuthProvider if:
 * 1. User is already authenticated with Descope (preserves session even if feature flag changes), OR
 * 2. Descope feature flag is enabled AND OTP feature flag is off (allows new users to log in)
 */
export const DescopeAuthProvider: FunctionComponent<PropsWithChildren> = ({
  children,
}) => {
  const { isProduction, application } = EnvService.get();

  // Check if user logged in with Descope (preserves session even if feature flag changes)
  const idpProvider =
    typeof window !== 'undefined' ? localStorage.getItem(IdpProviderKey) : null;
  const isDescopeUser = idpProvider === IDP_PROVIDER.DESCOPE;

  // Check feature flags
  const useOtpFallback = useGetFeatureFlag(FLAG.AUTH_OTP_FALLBACK, false, {
    env: application,
  });

  // Get Descope project ID from environment
  const getDescopeProjectId = (): string => {
    const projectId = import.meta.env.VITE_DESCOPE_PROJECT_ID;
    if (!projectId) {
      Sentry.captureMessage(
        'VITE_DESCOPE_PROJECT_ID is not set. Descope authentication will not work.',
        'warning',
      );
    }
    return projectId || '';
  };

  // Get Descope custom domain from environment
  const getDescopeCustomDomain = (): string | undefined => {
    const customDomain = import.meta.env.VITE_DESCOPE_CUSTOM_DOMAIN;
    return customDomain || undefined;
  };

  const projectId = getDescopeProjectId();
  const baseUrl = getDescopeCustomDomain();

  // Render AuthProvider if:
  // 1. User is already authenticated with Descope, OR
  // 2. OTP feature flag is off (Descope is default)
  // AND projectId is provided
  const shouldRenderAuthProvider =
    (isDescopeUser || !useOtpFallback) && projectId;

  if (!shouldRenderAuthProvider) {
    return children;
  }

  return (
    <AuthProvider
      projectId={projectId}
      baseUrl={baseUrl}
      sessionTokenViaCookie={{
        secure: isProduction,
      }}
    >
      <DescopeSessionTracker>{children}</DescopeSessionTracker>
    </AuthProvider>
  );
};
